/**
 * precisionDataCache
 *
 * A lightweight in-memory TTL cache shared across all Precision Planning pages.
 *
 * Problem it solves
 * ─────────────────
 * The four precision pages (VRA, Yield, Suitability, Land Expansion) all need
 * the same three base datasets: boundaries, all-anomalies, and missions.
 * When a user clicks through the nav quickly, each page fired its own parallel
 * requests, pushing the cumulative burst over the backend rate limit (100 req /
 * 15 min per IP).
 *
 * Solution
 * ────────
 * • boundaries, anomalies, and missions are cached for SHARED_TTL_MS (60 s).
 *   All four pages share a single in-flight promise so even a rapid nav sequence
 *   that triggers simultaneous mounts only makes ONE request per dataset.
 * • Per-field imagery is cached per boundary ID for IMAGERY_TTL_MS (120 s).
 * • A small INTER_BATCH_DELAY_MS pause between imagery batch rounds further
 *   spreads the requests over time.
 */

import api, { satelliteImageryApi, anomalyApi } from './api';

// ─── Configuration ─────────────────────────────────────────────────────────────

const SHARED_TTL_MS      = 60_000;   // boundaries / anomalies / missions: 60 s
const IMAGERY_TTL_MS     = 120_000;  // per-field imagery: 2 min
const IMAGERY_BATCH_SIZE = 3;        // max concurrent imagery requests per round
export const INTER_BATCH_DELAY_MS = 150; // ms pause between imagery batch rounds

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface CachedField {
  id: string;
  name: string;
  area_hectares?: number;
  metadata?: Record<string, any>;
  geometry?: any;
}

export interface CachedImageryRecord {
  id: string;
  acquisition_date: string;
  satellite?: string;
  cloud_cover_percentage?: number;
  indices: { index_type: string; mean_value: number }[];
}

export interface CachedAnomaly {
  id: string;
  boundary_id: string;
  boundary_name?: string;
  anomaly_type?: string;
  severity: string;
  status: string;
  change_percentage?: number | string;
  affected_area_hectares?: number;
  detection_date?: string;
  resolution_notes?: string;
}

export interface CachedMission {
  id: string;
  name: string;
  status: string;
  mission_type: string;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, any>;
}

// ─── Internal cache entries ────────────────────────────────────────────────────

interface Entry<T> {
  data: T;
  expiresAt: number;
  /** Shared in-flight promise so concurrent callers await the same fetch */
  inflight?: Promise<T>;
}

const _boundaries: Partial<Entry<CachedField[]>>           = {};
const _anomalies:  Partial<Entry<CachedAnomaly[]>>         = {};
const _missions:   Partial<Entry<CachedMission[]>>         = {};
const _imagery:    Map<string, Partial<Entry<CachedImageryRecord[]>>> = new Map();

/** Stable cache key that incorporates the requested record limit. */
function imageryKey(boundaryId: string, limit: number): string {
  return `${boundaryId}:${limit}`;
}

// ─── Generic TTL-aware fetch helper ───────────────────────────────────────────

function isFresh<T>(entry: Partial<Entry<T>>): entry is Entry<T> {
  return !!entry.data && Date.now() < (entry.expiresAt ?? 0);
}

// ─── Public API ────────────────────────────────────────────────────────────────

/** Returns all field boundaries, using the shared cache. */
export async function getBoundaries(): Promise<CachedField[]> {
  if (isFresh(_boundaries)) return _boundaries.data;

  if (_boundaries.inflight) return _boundaries.inflight;

  _boundaries.inflight = api.get('/satellite/boundaries')
    .then(res => {
      const raw = res.data?.data?.boundaries ?? res.data?.boundaries ?? res.data;
      const data: CachedField[] = Array.isArray(raw) ? raw : [];
      _boundaries.data      = data;
      _boundaries.expiresAt = Date.now() + SHARED_TTL_MS;
      _boundaries.inflight  = undefined;
      return data;
    })
    .catch(err => {
      _boundaries.inflight = undefined;
      throw err;
    });

  return _boundaries.inflight;
}

/** Returns all anomalies for the org, using the shared cache. */
export async function getAllAnomalies(): Promise<CachedAnomaly[]> {
  if (isFresh(_anomalies)) return _anomalies.data;

  if (_anomalies.inflight) return _anomalies.inflight;

  _anomalies.inflight = anomalyApi.getAnomalies()
    .then(res => {
      const raw = (res as any).anomalies ?? res.data?.anomalies ?? res.data;
      const data: CachedAnomaly[] = Array.isArray(raw) ? raw : [];
      _anomalies.data      = data;
      _anomalies.expiresAt = Date.now() + SHARED_TTL_MS;
      _anomalies.inflight  = undefined;
      return data;
    })
    .catch(err => {
      _anomalies.inflight = undefined;
      throw err;
    });

  return _anomalies.inflight;
}

/** Returns all missions for the org, using the shared cache. */
export async function getAllMissions(): Promise<CachedMission[]> {
  if (isFresh(_missions)) return _missions.data;

  if (_missions.inflight) return _missions.inflight;

  _missions.inflight = api.get('/missions', { params: { limit: 100 } })
    .then(res => {
      const raw = res.data?.data?.missions ?? res.data?.data ?? res.data;
      const data: CachedMission[] = Array.isArray(raw) ? raw : [];
      _missions.data      = data;
      _missions.expiresAt = Date.now() + SHARED_TTL_MS;
      _missions.inflight  = undefined;
      return data;
    })
    .catch(err => {
      _missions.inflight = undefined;
      throw err;
    });

  return _missions.inflight;
}

/** Returns imagery records for a single boundary, using the per-field cache. */
export async function getFieldImagery(
  boundaryId: string,
  limit = 50,
): Promise<CachedImageryRecord[]> {
  const key = imageryKey(boundaryId, limit);
  let entry = _imagery.get(key) ?? {};

  if (isFresh(entry)) return entry.data;

  if (entry.inflight) return entry.inflight;

  entry.inflight = satelliteImageryApi.getImagery(boundaryId, { limit })
    .then(res => {
      const raw = res.data?.imagery ?? res.data;
      const data: CachedImageryRecord[] = Array.isArray(raw) ? raw : [];
      const fresh: Entry<CachedImageryRecord[]> = {
        data,
        expiresAt: Date.now() + IMAGERY_TTL_MS,
      };
      _imagery.set(key, fresh);
      return data;
    })
    .catch(err => {
      entry.inflight = undefined;
      _imagery.set(key, entry);
      throw err;
    });

  _imagery.set(key, entry);
  return entry.inflight;
}

/**
 * Fetches imagery for an array of fields in serial batches of IMAGERY_BATCH_SIZE.
 * Respects the shared cache — already-cached fields cost 0 extra requests.
 * Returns a Map<fieldId, CachedImageryRecord[]>.
 */
export async function getBatchedImagery(
  fields: CachedField[],
  limit = 50,
): Promise<Map<string, CachedImageryRecord[]>> {
  const result = new Map<string, CachedImageryRecord[]>();

  for (let i = 0; i < fields.length; i += IMAGERY_BATCH_SIZE) {
    const batch = fields.slice(i, i + IMAGERY_BATCH_SIZE);

    const batchResults = await Promise.all(
      batch.map(async field => {
        try {
          const records = await getFieldImagery(field.id, limit);
          return { id: field.id, records };
        } catch {
          return { id: field.id, records: [] as CachedImageryRecord[] };
        }
      })
    );

    batchResults.forEach(r => result.set(r.id, r.records));

    // Pause between rounds only when there are more batches to go
    if (i + IMAGERY_BATCH_SIZE < fields.length) {
      await new Promise(resolve => setTimeout(resolve, INTER_BATCH_DELAY_MS));
    }
  }

  return result;
}

/** Force-expire all cached data (call from Refresh buttons). */
export function invalidateCache(): void {
  _boundaries.data      = undefined;
  _boundaries.expiresAt = 0;
  _anomalies.data       = undefined;
  _anomalies.expiresAt  = 0;
  _missions.data        = undefined;
  _missions.expiresAt   = 0;
  _imagery.clear();
}
